import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import FaqItem from "./components/FaqItem";
import { initLandingAnimations } from "./landingAnimations";
import "./LandingPage.css";
import shot1 from "./assets/landing/shot-1.jpg";
import shot2 from "./assets/landing/shot-2.jpg";
import shot3 from "./assets/landing/shot-3.jpg";
import shot4 from "./assets/landing/shot-4.jpg";
import shot5 from "./assets/landing/shot-5.jpg";
import shot6 from "./assets/landing/shot-6.jpg";
import shot7 from "./assets/landing/shot-7.jpg";
import shot8 from "./assets/landing/shot-8.jpg";
import shot9 from "./assets/landing/shot-9.jpg";
import proofA from "./assets/landing/proof-a.jpg";
import proofB from "./assets/landing/proof-b.jpg";
import proofC from "./assets/landing/proof-c.jpg";

/* Real photography (Unsplash-licensed, see assets/landing/SOURCES.md) for
   the hero gallery mockup; the CSS gradients behind them stay as loading
   placeholders. */
const heroShots = [shot1, shot2, shot3, shot4, shot5, shot6, shot7, shot8, shot9];

// Helper: get CMS value with fallback
const cms = (content, section, key, fallback) =>
  (content && content[section] && content[section][key]) || fallback;

/* Splits a headline into masked word spans GSAP can raise one by one.
   Rendered by React (not SplitText) so CMS re-renders never fight the DOM.
   The last word carries the violet→gold accent. */
const HeroWords = ({ text }) => {
  const words = String(text).split(/\s+/).filter(Boolean);
  return words.map((word, i) => (
    <React.Fragment key={`${word}-${i}`}>
      <span className={`lp-word${i === words.length - 1 ? " lp-word--accent" : ""}`}>
        <span className="lp-word__inner">{word}</span>
      </span>
      {i < words.length - 1 ? " " : null}
    </React.Fragment>
  ));
};

const LandingPage = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  // Dark ("Midnight") is the default; light only when explicitly chosen.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("lp-theme") !== "light");
  const [cmsContent, setCmsContent] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/landing-content`)
      .then((r) => r.json())
      .then((data) => setCmsContent(data))
      .catch(() => {});
  }, []);

  // Lock background scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileMenu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenu]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMobileMenu(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // GSAP motion layer — built once on mount, torn down on unmount.
  useLayoutEffect(() => {
    animRef.current = initLandingAnimations(rootRef.current);
    return () => animRef.current?.cleanup();
  }, []);

  // Async content (plans, CMS copy) renders new cards after mount — arm
  // their scroll reveals and let ScrollTrigger re-measure the page.
  useEffect(() => {
    animRef.current?.refresh();
  }, [cmsContent]);

  // Arriving from /pricing (or a shared link) with a #section hash — scroll
  // there once the page has painted.
  useEffect(() => {
    const id = (window.location.hash || "").replace("#", "");
    if (!id) return;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, []);

  const toggleTheme = () => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("lp-theme", next ? "dark" : "light");
      return next;
    });
  };

  const scrollTo = (id) => {
    setMobileMenu(false);
    if (id === "pricing") {
      navigate("/pricing");
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navItems = [
    ["features", "Features"],
    ["album-review", "Album Review"],
    ["services", "Services"],
    ["pricing", "Pricing"],
    ["testimonials", "Stories"],
    ["faq", "FAQ"],
  ];

  const featureDefaults = [
    {
      title: "Smart Photo Upload",
      desc: "Drag & drop bulk uploads with automatic compression. Pause, resume, and skip duplicates intelligently.",
    },
    {
      title: "AI Facial Recognition",
      desc: "Find any person across thousands of photos in seconds. Our AI detects and matches faces with 99% accuracy.",
    },
    {
      title: "Client Galleries",
      desc: "Beautiful, branded galleries with swipe-to-select voting. Clients choose their favorites effortlessly.",
    },
    {
      title: "Event Management",
      desc: "Create and organize events with custom details. Track every shoot from booking to final delivery.",
    },
    {
      title: "Photo Selection Workflow",
      desc: "Clients swipe right to select, left to unselect. Every photo starts unselected — they only pick the keepers.",
    },
    {
      title: "ZIP Download & Share",
      desc: "One-click download of entire galleries as ZIP. Generate shareable links for clients instantly.",
    },
    {
      title: "Photo Frame Orders",
      desc: "Clients order printed frames directly from their gallery. Choose size, finish, and quantity — delivered to their door.",
    },
    {
      title: "Contract e-Signing",
      desc: "Send, sign, and store contracts digitally for every event. No printing, no scanning — legally binding signatures in seconds.",
    },
    {
      title: "PDF Album Proofing",
      desc: "Share album designs as a link. Clients mark up pages, leave notes, and submit corrections — all without a single email attachment.",
    },
    {
      title: "Studio Website Builder",
      desc: "Get a free, professional website for your studio in minutes. Showcase your portfolio and attract new clients — no coding needed.",
    },
    {
      title: "QR Gallery Sharing",
      desc: "Generate a QR code for any event gallery. Guests scan and instantly access their photos — no app download or login required.",
    },
    {
      title: "Live Slideshow",
      desc: "Display a live, real-time slideshow at your event venue. Photos appear on screen the moment they're uploaded — wow your clients on the spot.",
    },
    {
      title: "Invoice Generation",
      desc: "Create and send professional invoices for every event in seconds. Track payment status and keep your billing organised in one place.",
    },
    {
      title: "Photentra AI",
      desc: "Your built-in AI assistant for photography workflows. Get smart suggestions, automate repetitive tasks, and work faster than ever.",
    },
    {
      title: "Client Reminders",
      desc: "Send automated reminders to clients for gallery reviews, contract signing, and payments. Never chase a client manually again.",
    },
    {
      title: "Team Management",
      desc: "Invite photographers and assistants to collaborate on events. Manage roles and permissions across your entire studio team.",
    },
    {
      title: "Portfolio Page",
      desc: "Showcase your best work with a stunning public portfolio. Share a single link with potential clients and let your photos do the talking.",
    },
    // Appended, never inserted mid-array: the CMS overrides key cards by index
    // (title_N/desc_N), so a new card in the middle would re-point every key
    // after it at the wrong card.
    {
      title: "Client CRM & Quotes",
      desc: "Track every enquiry from first call to booking on a drag-and-drop pipeline. Send branded quotes built from your own packages — clients accept with one tap.",
      highlight: true,
      badge: "New · Pro",
    },
  ];

  const featureIcons = [
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 22V8m0 0-5 5m5-5 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 20v3a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3v-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="1.6"/><circle cx="16" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.6"/><path d="M10.4 24a6 6 0 0 1 11.2 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M4 10V6a2 2 0 0 1 2-2h4M28 10V6a2 2 0 0 0-2-2h-4M4 22v4a2 2 0 0 0 2 2h4M28 22v4a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="4" y="6" width="24" height="20" rx="3" stroke="currentColor" strokeWidth="1.6"/><path d="M4 20.5 10.5 15l5 4.5L21 13l7 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="11.5" cy="11.5" r="1.8" stroke="currentColor" strokeWidth="1.6"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="4" y="6" width="24" height="22" rx="3" stroke="currentColor" strokeWidth="1.6"/><path d="M4 13h24M11 4v5M21 4v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="m12 20 2.5 2.5L21 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="m6 16 4 4 8-8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 22h12M14 26h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M22 6h4M24 4v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 5v14m0 0-5.5-5.5M16 19l5.5-5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 21v2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="4" y="4" width="24" height="24" rx="2" stroke="currentColor" strokeWidth="1.6"/><rect x="8" y="8" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="1.6"/><path d="M8 20.5 13 15l4 4 3-3.5 4 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="13.5" cy="12.5" r="1.5" stroke="currentColor" strokeWidth="1.6"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="5" y="3" width="18" height="24" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M9 9h10M9 13h10M9 17h6" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6"/><path d="M19 22c0 0 1.5-1.5 3-1.5S25 22 25 22v5l-3-1.5L19 27v-5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="3" y="5" width="26" height="20" rx="2" stroke="currentColor" strokeWidth="1.6"/><line x1="16" y1="5" x2="16" y2="25" stroke="currentColor" strokeWidth="1.6"/><path d="M6 10h7M6 14h7M6 18h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6"/><path d="M19 10h7M19 14h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6"/><circle cx="22" cy="19" r="2" stroke="currentColor" strokeWidth="1.4"/><path d="M20.5 20.5 19 22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="3" y="6" width="26" height="20" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 11h26" stroke="currentColor" strokeWidth="1.6"/><circle cx="7.5" cy="8.5" r="1" fill="currentColor"/><circle cx="11" cy="8.5" r="1" fill="currentColor"/><circle cx="14.5" cy="8.5" r="1" fill="currentColor"/><path d="M8 17h6M8 21h10" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6"/><rect x="18" y="15" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.6"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="4" y="4" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="7" y="7" width="4" height="4" fill="currentColor" rx=".5"/><rect x="18" y="4" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="21" y="7" width="4" height="4" fill="currentColor" rx=".5"/><rect x="4" y="18" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="7" y="21" width="4" height="4" fill="currentColor" rx=".5"/><path d="M18 18h3v3h-3zM21 21h3v3h-3zM24 18h4M18 24v4M24 24h4M24 28h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="2" y="7" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M24 13l6-4v14l-6-4v-6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="11" cy="15" r="3" stroke="currentColor" strokeWidth="1.6"/><path d="M5 20.5l3-3 3 2.5 3-3.5 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="5" y="3" width="18" height="24" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M9 9h10M9 13h10M9 17h6M9 21h8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6"/><path d="M20 19l4 2-4 2v-4Z" fill="currentColor"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.6"/><path d="M12 16h8M16 12v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M9 9c1.5-1.5 3.5-2.5 7-2.5s5.5 1 7 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="1.4"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 4C9.4 4 4 9.4 4 16s5.4 12 12 12 12-5.4 12-12S22.6 4 16 4Z" stroke="currentColor" strokeWidth="1.6"/><path d="M16 4v12l8 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.6"/><circle cx="22" cy="10" r="4" stroke="currentColor" strokeWidth="1.6"/><circle cx="16" cy="22" r="4" stroke="currentColor" strokeWidth="1.6"/><path d="M14 10h4M13.5 13.5l-2 5M18.5 13.5l2 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="4" y="6" width="24" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M4 18.5 10 13l5 4.5 5-6 8 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="11" cy="11" r="2" stroke="currentColor" strokeWidth="1.4"/><path d="M10 28h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M16 24v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.6"/><path d="M4 22a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><rect x="18" y="6" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><path d="M21 10h4M21 13h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="m20 24 2.5 2.5L27 22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  ];

  /* Album review (PDF proofing) — the capability that replaces the
     "v3_final_FINAL.pdf" email thread most studios still live in. */
  const albumReviewDefaults = [
    {
      title: "Mark up the page itself",
      desc: "Clients circle a crop, arrow a swap, or scribble a note straight onto the spread — six pen colours, any brush size.",
    },
    {
      title: "A comment on every page",
      desc: "Written notes sit alongside the markup, page by page, so a correction never gets buried in a reply chain.",
    },
    {
      title: "Reviewed across several sittings",
      desc: "Drafts save as they go. Clients close the tab, come back tomorrow, and pick up exactly where they stopped.",
    },
    {
      title: "Locked the moment it's submitted",
      desc: "A submitted review freezes, so you and your client are always looking at the same version of the truth.",
    },
  ];

  const serviceDefaults = [
    { emoji: "📸", title: "Wedding Photography", desc: "Manage thousands of wedding photos effortlessly. Let couples find their moments with AI face search and share galleries with guests." },
    { emoji: "👶", title: "Portrait & Family Studios", desc: "Organize portrait sessions, share proofs with clients, and let them pick their favorites through beautiful voting galleries." },
    { emoji: "🏢", title: "Corporate & Events", desc: "Handle corporate events, conferences, and galas. Attendees find their own photos using facial recognition — no manual tagging needed." },
    { emoji: "🎓", title: "School & Graduation", desc: "Process entire school photo days in minutes. Parents find their children instantly with AI face matching across thousands of images." },
    { emoji: "🏟️", title: "Sports Photography", desc: "Capture action at scale. Athletes and parents locate their photos by face, even across multi-day tournaments with thousands of shots." },
    { emoji: "🌍", title: "Destination & Travel", desc: "Work from anywhere with cloud-based management. Upload on location, deliver globally, and manage clients across time zones." },
  ];

  const stepDefaults = [
    { title: "Upload Photos", desc: "Drag & drop your photos. We handle compression, deduplication, and organization automatically." },
    { title: "AI Processes", desc: "Our AI scans every face, generates embeddings, and indexes your photos for instant search." },
    { title: "Share with Clients", desc: "Generate a unique link. Your clients browse, face-search, and vote on their favorites." },
    { title: "Deliver & Download", desc: "Clients download selected photos as ZIP. You track selections and finalize delivery." },
  ];

  const testimonialDefaults = [
    { quote: "Photentra completely transformed how we deliver wedding photos. Our clients love the face search feature — they find their photos in seconds instead of scrolling through thousands.", name: "Sarah Mitchell", role: "Wedding Photographer, NYC", avatar: "SM" },
    { quote: "We process 50+ corporate events per month. The bulk upload with pause/resume and duplicate skipping saves us hours every single week. It's a game changer.", name: "James Rodriguez", role: "Corporate Media, London", avatar: "JR" },
    { quote: "The client gallery with swipe voting is incredibly intuitive. Our approval rates went up 40% because clients actually enjoy selecting their photos now.", name: "Priya Sharma", role: "Portrait Studio, Mumbai", avatar: "PS" },
    { quote: "As a school photographer, I deal with thousands of kids each season. The AI face recognition lets parents find their child's photos instantly. Support has been fantastic too.", name: "Michael Chen", role: "School Photography, Sydney", avatar: "MC" },
    { quote: "We switched from 3 different tools to just Photentra. Upload, organize, share, deliver — everything in one place. Our workflow is so much cleaner now.", name: "Emma Johansson", role: "Event Studio, Stockholm", avatar: "EJ" },
    { quote: "The ZIP download feature is exactly what we needed. Clients get their selected photos in one click. No more back-and-forth emails with WeTransfer links.", name: "David Okafor", role: "Freelance Photographer, Lagos", avatar: "DO" },
  ];

  const faqDefaults = [
    { q: "How does the AI facial recognition work?", a: "When you upload photos, our AI automatically detects and creates face embeddings for every person. Clients can then upload a selfie to find all their photos across the entire event — matching with over 99% accuracy." },
    { q: "Is there a limit on photo uploads?", a: "Starter supports up to 1,000 photos per event. Pro supports up to 3,000 photos per event. Pro Max supports up to 2,500 photos per event at original quality. Pro Max Ultra offers completely unlimited uploads with 1 TB of storage." },
    { q: "Can my clients access galleries without creating an account?", a: "Yes! You generate a unique shareable link for each event. Clients simply open the link — no account or app download required." },
    { q: "How does the client voting/selection work?", a: "Clients view photos in a beautiful gallery. Every photo starts unselected — they tap or swipe right to select the ones they want, and left to unselect. You see their picks organized in folders, making delivery a breeze." },
    { q: "Is my data secure?", a: "Absolutely. All photos are stored on AWS S3 with encryption. We use HTTPS everywhere, and your data is never shared with third parties." },
    { q: "Can I cancel my subscription anytime?", a: "Yes, you can cancel anytime with no questions asked. Your data remains accessible for 30 days after cancellation." },
  ];

  return (
    <div className={`lp${darkMode ? " lp--dark" : ""}`} ref={rootRef}>
      <div className="lp-gridbg" aria-hidden="true" />
      <div className="lp-grain" aria-hidden="true" />
      <span className="lp-progress" aria-hidden="true" />

      {/* ────────── HEADER ────────── */}
      <header className={`lp-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="lp-header__inner">
          <button
            className="lp-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Back to top"
          >
            <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" className="lp-logo__img" />
          </button>

          <nav className="lp-nav" aria-label="Primary">
            {navItems.map(([id, label]) => (
              <button key={id} className="lp-nav__link" onClick={() => scrollTo(id)}>
                {label}
              </button>
            ))}
          </nav>

          <div className="lp-header__actions">
            <button className="lp-icon-btn" onClick={toggleTheme} aria-label="Toggle colour theme">
              {darkMode ? (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6"/><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
              )}
            </button>
            <button className="lp-btn lp-btn--ghost lp-hide-sm" onClick={() => navigate("/login")}>Log In</button>
            <button className="lp-btn lp-btn--primary" onClick={() => navigate("/signup")}>Get Started</button>
          </div>

          <button
            className={`lp-hamburger${mobileMenu ? " is-open" : ""}`}
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenu}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ────────── MOBILE DRAWER ────────── */}
      <div
        className={`lp-drawer-backdrop${mobileMenu ? " is-open" : ""}`}
        onClick={() => setMobileMenu(false)}
        aria-hidden="true"
      />
      <aside className={`lp-drawer${mobileMenu ? " is-open" : ""}`} aria-hidden={!mobileMenu}>
        <nav className="lp-drawer__nav">
          {navItems.map(([id, label], i) => (
            <button
              key={id}
              className="lp-drawer__link"
              style={{ transitionDelay: `${mobileMenu ? 60 + i * 40 : 0}ms` }}
              onClick={() => scrollTo(id)}
            >
              <span className="lp-drawer__idx">0{i + 1}</span>
              {label}
            </button>
          ))}
        </nav>
        <div className="lp-drawer__actions">
          <button className="lp-btn lp-btn--outline lp-btn--block" onClick={() => { setMobileMenu(false); navigate("/login"); }}>
            Log In
          </button>
          <button className="lp-btn lp-btn--primary lp-btn--block" onClick={() => { setMobileMenu(false); navigate("/signup"); }}>
            Get Started
          </button>
        </div>
      </aside>

      {/* ────────── HERO ────────── */}
      <section className="lp-hero">
        <div className="lp-hero__aura" aria-hidden="true">
          <span className="lp-aura lp-aura--a" />
          <span className="lp-aura lp-aura--b" />
          <span className="lp-beam lp-beam--a" />
          <span className="lp-beam lp-beam--b" />
          <span className="lp-beam lp-beam--c" />
        </div>

        <div className="lp-hero__inner">
          <div className="lp-hero__copy">
            <div className="lp-eyebrow">
              <span className="lp-eyebrow__dot" />
              {cms(cmsContent, "hero", "badge", "Trusted by 2,000+ Studios Worldwide")}
            </div>

            <h1 className="lp-hero__title">
              <HeroWords text={cms(cmsContent, "hero", "title_line1", "Studio in your pocket")} />
            </h1>

            <p className="lp-hero__lede">
              {cms(cmsContent, "hero", "subtitle", "AI-powered photo management, facial recognition, client galleries, and seamless delivery — all in one platform built for professional photographers.")}
            </p>

            <ul className="lp-checks">
              {[
                ["bullet_1", "AI face search that finds anyone across thousands of photos"],
                ["bullet_2", "Branded client galleries with swipe-to-select proofing"],
                ["bullet_3", "Contracts, invoices & albums — delivered from one place"],
              ].map(([key, fallback]) => (
                <li key={key}>
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {cms(cmsContent, "hero", key, fallback)}
                </li>
              ))}
            </ul>

            <div className="lp-hero__ctas">
              <button className="lp-btn lp-btn--primary lp-btn--lg" data-magnetic onClick={() => navigate("/signup")}>
                {cms(cmsContent, "hero", "cta_primary", "Get Started")}
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h11M11 5.5 15.5 10 11 14.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <button className="lp-btn lp-btn--outline lp-btn--lg" onClick={() => scrollTo("features")}>
                {cms(cmsContent, "hero", "cta_secondary", "See How It Works")}
              </button>
            </div>

            <dl className="lp-stats">
              {[
                ["stat1_num", "2M+", "stat1_label", "Photos Managed"],
                ["stat2_num", "2,000+", "stat2_label", "Studios Worldwide"],
                ["stat3_num", "50+", "stat3_label", "Countries"],
              ].map(([numKey, numDef, labelKey, labelDef]) => (
                <div className="lp-stat" key={numKey}>
                  <dt className="lp-stat__num" data-count>{cms(cmsContent, "hero", numKey, numDef)}</dt>
                  <dd className="lp-stat__label">{cms(cmsContent, "hero", labelKey, labelDef)}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Product visual — a gallery panel mid face-search */}
          <div className="lp-hero__visual">
            <div className="lp-panel">
              <div className="lp-panel__head">
                <div className="lp-panel__title">
                  <span className="lp-panel__dot" />
                  <span className="lp-panel__label">Riverside Wedding · 2,481 photos</span>
                </div>
                <div className="lp-panel__search">
                  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.4"/><path d="m10.6 10.6 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  Face search
                </div>
              </div>

              <div className="lp-panel__grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <figure
                    key={i}
                    className={`lp-shot lp-shot--${i}${i === 2 ? " is-matched" : ""}${i === 5 ? " is-picked" : ""}`}
                    style={{ animationDelay: `${i * 90}ms` }}
                  >
                    <img src={heroShots[i - 1]} alt="" loading="lazy" draggable={false} />
                    {i === 2 && (
                      <>
                        <span className="lp-shot__bracket" />
                        <span className="lp-shot__match">98.6% match</span>
                      </>
                    )}
                    {i === 5 && <span className="lp-shot__pick">✓</span>}
                  </figure>
                ))}
                <span className="lp-panel__scan" aria-hidden="true" />
              </div>

              <div className="lp-panel__foot">
                <span className="lp-chip lp-chip--ok">142 selected</span>
                <span className="lp-chip">38 not selected</span>
                <span className="lp-panel__spacer" />
                <span className="lp-chip lp-chip--solid">Deliver ZIP</span>
              </div>
            </div>

            <div className="lp-float lp-float--faces">
              <div className="lp-float__icon">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6"/><circle cx="12" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.6"/><path d="M7.6 17.4a5 5 0 0 1 8.8 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              </div>
              <div>
                <strong>1,204 faces</strong>
                <span>indexed in 38s</span>
              </div>
            </div>

            <div className="lp-float lp-float--deliver">
              <div className="lp-float__spark" />
              <div>
                <strong>Gallery shared</strong>
                <span>with 6 guests</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── CAPABILITIES RIBBON ────────── */}
      <div className="lp-band" aria-hidden="true">
        <div className="lp-band__track">
          {[0, 1].map((copy) =>
            [
              "AI Face Search", "Client Galleries", "Live Slideshow", "QR Sharing",
              "Album Proofing", "Contracts & Invoices", "Studio Websites",
              "Client CRM", "RAW Backup", "Team Tools",
            ].map((item, i) => (
              <span className="lp-band__item" key={`${copy}-${i}`}>
                {item}
                <i className="lp-band__star">✦</i>
              </span>
            ))
          )}
        </div>
      </div>

      {/* ────────── FEATURES (BENTO) ────────── */}
      <section className="lp-section" id="features">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "features", "section_tag", "Features")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "features", "section_title", "Everything You Need to Run Your Studio")}
          </h2>
          <p className="lp-shead__desc">
            {cms(cmsContent, "features", "section_desc", "From upload to delivery, Photentra handles every step of your photography workflow with intelligent automation and beautiful client experiences.")}
          </p>
        </header>

        <div className="lp-bento">
          {featureDefaults.map((f, i) => (
            <article
              key={i}
              className={`lp-bento__card${i < 2 || f.highlight ? " lp-bento__card--wide" : ""}${f.highlight ? " lp-bento__card--highlight" : ""}`}
              data-reveal
            >
              {f.badge && <span className="lp-bento__badge">{f.badge}</span>}
              <div className="lp-bento__icon">{featureIcons[i]}</div>
              <h3 className="lp-bento__title">{cms(cmsContent, "features", `title_${i + 1}`, f.title)}</h3>
              <p className="lp-bento__desc">{cms(cmsContent, "features", `desc_${i + 1}`, f.desc)}</p>
              {(i < 2 || f.highlight) && <span className="lp-bento__glow" aria-hidden="true" />}
            </article>
          ))}
        </div>
      </section>

      {/* ────────── ALBUM REVIEW (PDF PROOFING) ────────── */}
      <section className="lp-section lp-section--showcase lp-invert" id="album-review">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "album_review", "section_tag", "Album Review")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "album_review", "section_title", "Album Proofing Without the Email Thread")}
          </h2>
          <p className="lp-shead__desc">
            {cms(cmsContent, "album_review", "section_desc", "Send the album design as a link. Your client draws corrections directly on the pages, leaves a note on each one, and submits — every change lands back with you in one place.")}
          </p>
        </header>

        <div className="lp-review">
          {/* Product visual — an album spread mid-markup. Not [data-reveal]:
              its motion (parallax + fade) lives in buildParallax. */}
          <div className="lp-review__visual">
            <div className="lp-proof">
              <div className="lp-proof__head">
                <span className="lp-proof__dot" />
                <span className="lp-proof__label">Mehta Wedding · Album v2</span>
                <span className="lp-proof__page">Page 7 / 24</span>
              </div>

              <div className="lp-proof__spread">
                <div className="lp-proof__leaf">
                  <img className="lp-proof__img lp-proof__img--a" src={proofA} alt="" loading="lazy" draggable={false} />
                  <img className="lp-proof__img lp-proof__img--b" src={proofB} alt="" loading="lazy" draggable={false} />
                </div>
                <div className="lp-proof__leaf">
                  <img className="lp-proof__img lp-proof__img--c" src={proofC} alt="" loading="lazy" draggable={false} />

                  {/* The client's ink, drawn on as you watch */}
                  <svg className="lp-proof__ink" viewBox="0 0 200 150" fill="none" aria-hidden="true">
                    <ellipse
                      cx="112" cy="66" rx="46" ry="34"
                      stroke="#ff2d2d" strokeWidth="3.2" strokeLinecap="round"
                      strokeDasharray="252" strokeDashoffset="252"
                    />
                    <path
                      d="M40 122c14-10 26-16 38-19"
                      stroke="#ff2d2d" strokeWidth="3.2" strokeLinecap="round"
                      strokeDasharray="60" strokeDashoffset="60"
                    />
                  </svg>

                  <span className="lp-proof__note">Can we crop tighter here?</span>
                </div>
              </div>

              <div className="lp-proof__foot">
                <span className="lp-proof__pens" aria-hidden="true">
                  {["#ff0000", "#0066ff", "#00cc44", "#ff9900", "#9900ff", "#000000"].map((c) => (
                    <i key={c} style={{ background: c }} />
                  ))}
                </span>
                <span className="lp-panel__spacer" />
                <span className="lp-chip lp-chip--solid">Submit review</span>
              </div>
            </div>

            <div className="lp-float lp-float--draft">
              <div className="lp-float__spark" />
              <div>
                <strong>Draft saved</strong>
                <span>9 pages marked up</span>
              </div>
            </div>
          </div>

          <ol className="lp-review__list">
            {albumReviewDefaults.map((f, i) => (
              <li
                key={i}
                className="lp-review__item"
                data-reveal
              >
                <span className="lp-review__num">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="lp-review__title">
                    {cms(cmsContent, "album_review", `title_${i + 1}`, f.title)}
                  </h3>
                  <p className="lp-review__desc">
                    {cms(cmsContent, "album_review", `desc_${i + 1}`, f.desc)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ────────── SERVICES ────────── */}
      <section className="lp-section lp-section--alt" id="services">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "services", "section_tag", "Services")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "services", "section_title", "Built for Photography Studios Worldwide")}
          </h2>
          <p className="lp-shead__desc">
            {cms(cmsContent, "services", "section_desc", "Whether you're a solo photographer or a large studio, Photentra scales with your business.")}
          </p>
        </header>

        <div className="lp-grid-3">
          {serviceDefaults.map((s, i) => (
            <article key={i} className="lp-svc" data-reveal>
              <span className="lp-svc__emoji">{cms(cmsContent, "services", `emoji_${i + 1}`, s.emoji)}</span>
              <h3 className="lp-svc__title">{cms(cmsContent, "services", `title_${i + 1}`, s.title)}</h3>
              <p className="lp-svc__desc">{cms(cmsContent, "services", `desc_${i + 1}`, s.desc)}</p>
              <span className="lp-svc__rule" aria-hidden="true" />
            </article>
          ))}
        </div>
      </section>

      {/* ────────── HOW IT WORKS ────────── */}
      <section className="lp-section lp-section--alt lp-invert">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "steps", "section_tag", "How It Works")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "steps", "section_title", "From Shoot to Delivery in 4 Steps")}
          </h2>
        </header>

        <ol className="lp-steps">
          {stepDefaults.map((s, i) => (
            <li key={i} className="lp-step" data-reveal>
              <span className="lp-step__num">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="lp-step__title">{cms(cmsContent, "steps", `title_${i + 1}`, s.title)}</h3>
              <p className="lp-step__desc">{cms(cmsContent, "steps", `desc_${i + 1}`, s.desc)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ────────── TESTIMONIALS ────────── */}
      <section className="lp-section" id="testimonials">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "testimonials", "section_tag", "Testimonials")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "testimonials", "section_title", "Loved by Photographers Everywhere")}
          </h2>
        </header>

        {/* Two counter-flowing marquee rows; each track holds its cards twice
            so the loop is seamless. The duplicate copy is aria-hidden. */}
        <div className="lp-quotes" data-reveal>
          {[0, 1].map((rowIdx) => {
            const rowOrder = rowIdx === 0
              ? testimonialDefaults.map((_, i) => i)
              : testimonialDefaults.map((_, i) => (i + 3) % testimonialDefaults.length);
            return (
              <div className="lp-qrow" key={rowIdx}>
                <div className="lp-qrow__track">
                  {[0, 1].map((copy) =>
                    rowOrder.map((i) => {
                      const t = testimonialDefaults[i];
                      return (
                        <figure
                          key={`${copy}-${i}`}
                          className="lp-quote"
                          aria-hidden={copy === 1 || undefined}
                        >
                          <span className="lp-quote__mark" aria-hidden="true">&ldquo;</span>
                          <blockquote className="lp-quote__text">
                            {cms(cmsContent, "testimonials", `quote_${i + 1}`, t.quote)}
                          </blockquote>
                          <figcaption className="lp-quote__by">
                            <span className="lp-quote__avatar">{t.avatar}</span>
                            <span>
                              <strong>{cms(cmsContent, "testimonials", `name_${i + 1}`, t.name)}</strong>
                              <em>{cms(cmsContent, "testimonials", `role_${i + 1}`, t.role)}</em>
                            </span>
                          </figcaption>
                        </figure>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ────────── FAQ ────────── */}
      <section className="lp-section lp-section--alt lp-invert" id="faq">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "faq", "section_tag", "FAQ")}</span>
          <h2 className="lp-shead__title">
            {cms(cmsContent, "faq", "section_title", "Frequently Asked Questions")}
          </h2>
        </header>

        <div className="lp-faq">
          {faqDefaults.map((faq, i) => (
            <FaqItem
              key={i}
              question={cms(cmsContent, "faq", `question_${i + 1}`, faq.q)}
              answer={cms(cmsContent, "faq", `answer_${i + 1}`, faq.a)}
            />
          ))}
        </div>
      </section>

      {/* ────────── CTA ────────── */}
      <section className="lp-cta">
        <div className="lp-cta__aura" aria-hidden="true" />
        <div className="lp-cta__inner">
          <h2 className="lp-cta__title">
            {cms(cmsContent, "cta", "title", "Ready to Transform Your Photography Business?")}
          </h2>
          <p className="lp-cta__desc">
            {cms(cmsContent, "cta", "description", "Join thousands of photographers who deliver faster, impress clients, and grow their studios with Photentra.")}
          </p>
          <button className="lp-btn lp-btn--white lp-btn--lg" data-magnetic onClick={() => navigate("/signup")}>
            {cms(cmsContent, "cta", "button_text", "Get Started")}
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h11M11 5.5 15.5 10 11 14.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <p className="lp-cta__note">
            {cms(cmsContent, "cta", "note", "No credit card required · Free forever on Starter plan")}
          </p>
        </div>
      </section>

      {/* ────────── FOOTER ────────── */}
      <footer className="lp-footer">
        <div className="lp-footer__top" data-reveal>
          <div className="lp-footer__brand">
            <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" className="lp-logo__img" />
            <p className="lp-footer__tagline">
              AI-powered photo management for professional photographers and studios worldwide.
            </p>
            <div className="lp-social">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="2.5" y="2.5" width="19" height="19" rx="5"/><circle cx="12" cy="12" r="4.6"/><circle cx="17.6" cy="6.4" r="1.2" fill="currentColor" stroke="none"/></svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>

          <div className="lp-footer__cols">
            <div className="lp-footer__col">
              <h4>Product</h4>
              <button onClick={() => scrollTo("features")}>Features</button>
              <button onClick={() => navigate("/pricing")}>Pricing</button>
              <button onClick={() => scrollTo("services")}>Services</button>
              <button onClick={() => scrollTo("faq")}>FAQ</button>
            </div>
            <div className="lp-footer__col">
              <h4>Company</h4>
              <button>About Us</button>
              <button>Blog</button>
              <button>Careers</button>
              <button>Contact</button>
            </div>
            <div className="lp-footer__col">
              <h4>Legal</h4>
              <button onClick={() => navigate("/privacy")}>Privacy Policy</button>
              <button onClick={() => navigate("/terms")}>Terms of Service</button>
            </div>
          </div>
        </div>

        <div className="lp-footer__bottom">
          <p>&copy; {new Date().getFullYear()} Photentra AI Technologies PVT LTD. All rights reserved.</p>
          <p className="lp-footer__made">Crafted for photographers</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
